import { useCallback, useEffect, useState } from "react";
import { FormAlert } from "@NP-Webber/crm-kit";
import {
  financeApi,
  formatShekels,
  type Balance,
  type Subscription,
} from "../../api/financeApi";
import { financeStructureApi, type FinanceCategory } from "../../api/financeStructureApi";
import { toClientApiError } from "../../api/httpClient";

export type CustomerDebtsPanelProps = {
  customerId: string;
};

type DebtRow = {
  id: string;
  title: string;
  categoryName: string;
  amountAgorot: number;
  status: string;
};

export function CustomerDebtsPanel({ customerId }: CustomerDebtsPanelProps) {
  const [balance, setBalance] = useState<Balance | null>(null);
  const [debts, setDebts] = useState<DebtRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [bal, subs, cats] = await Promise.all([
        financeApi.getBalance(customerId),
        financeApi.listSubscriptions(customerId),
        financeStructureApi.listCustomerCategories(customerId).catch(() => [] as FinanceCategory[]),
      ]);
      setBalance(bal);
      const catNames = new Map(cats.map((c) => [c.id, c.name]));
      const open: DebtRow[] = (subs as Subscription[])
        .filter((sub) => sub.status === "active" || sub.status === "paused")
        .filter((sub) => bal.amountAgorot > 0)
        .map((sub) => ({
          id: sub.id,
          title: sub.title,
          categoryName: sub.categoryId
            ? catNames.get(sub.categoryId) ?? "—"
            : "—",
          amountAgorot: sub.amountAgorot,
          status: sub.status === "active" ? "פעיל" : "מושהה",
        }));
      // When balance > 0 show subscription lines as debt context; if no subs, one row from balance.
      if (bal.amountAgorot > 0 && open.length === 0) {
        setDebts([
          {
            id: "balance",
            title: "יתרת חוב",
            categoryName: "—",
            amountAgorot: bal.amountAgorot,
            status: "חייב לפורום",
          },
        ]);
      } else if (bal.amountAgorot > 0) {
        setDebts(open);
      } else {
        setDebts([]);
      }
    } catch (err) {
      setError(toClientApiError(err).message);
      setDebts([]);
      setBalance(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <p className="related-panel__empty">טוען חובות…</p>;
  }

  if (error) {
    return <FormAlert severity="error">{error}</FormAlert>;
  }

  if (!balance || balance.amountAgorot <= 0 || debts.length === 0) {
    return (
      <div className="related-panel" style={{ textAlign: "center", padding: 24 }}>
        <p className="related-panel__empty" style={{ fontWeight: 600, color: "var(--ck-success)" }}>
          אין חובות פתוחים
        </p>
      </div>
    );
  }

  return (
    <div className="related-panel">
      <p style={{ margin: "0 0 12px", fontSize: 13 }}>
        יתרת חוב לקופה:{" "}
        <strong style={{ color: "var(--ck-danger)" }}>
          {formatShekels(balance.amountAgorot)} ₪
        </strong>
      </p>
      <div style={{ overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>מנוי</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>אגף</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>חודשי</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>סטטוס</th>
            </tr>
          </thead>
          <tbody>
            {debts.map((row) => (
              <tr key={row.id} style={{ borderTop: "1px solid var(--ck-border)" }}>
                <td style={{ padding: "8px 10px", fontWeight: 600 }}>{row.title}</td>
                <td style={{ padding: "8px 10px" }}>{row.categoryName}</td>
                <td style={{ padding: "8px 10px" }}>{formatShekels(row.amountAgorot)} ₪</td>
                <td style={{ padding: "8px 10px" }}>{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
